import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { createTheme, PaletteMode } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

interface ColorSettings {
  primary: string;
  secondary: string;
}

type ThemeContextType = {
  mode: PaletteMode;
  toggleColorMode: () => void;
  colors: ColorSettings;
  setColors: (colors: ColorSettings) => void;
};

const defaultColors: ColorSettings = {
  primary: '#D7E5E8',
  secondary: '#32CD32'
};

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
  colors: defaultColors,
  setColors: () => {}
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Check for user's system preference
  const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  // Initialize theme based on system preference
  const [mode, setMode] = useState<PaletteMode>(prefersDarkMode ? 'dark' : 'light');
  
  // Initialize colors from localStorage or use defaults
  const [colors, setColors] = useState<ColorSettings>(() => {
    try {
      const savedColors = localStorage.getItem('themeColors');
      return savedColors ? JSON.parse(savedColors) : defaultColors;
    } catch (error) {
      console.error('Error loading theme colors from localStorage:', error);
      return defaultColors;
    }
  });

  // Listen for changes in system preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setMode(e.matches ? 'dark' : 'light');
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Toggle between light and dark mode
  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };
  
  // Save colors to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem('themeColors', JSON.stringify(colors));
    } catch (error) {
      console.error('Error saving theme colors to localStorage:', error);
    }
  }, [colors]);

  // Create theme based on current mode
  const theme = React.useMemo(() => {
    console.log('Creating theme with colors:', colors);
    return createTheme({
      palette: {
        mode,
        primary: {
          main: colors.primary,
        },
        secondary: {
          main: colors.secondary,
        },
        background: {
          default: mode === 'light' ? '#f5f5f5' : '#121212',
          paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
        },
      },
      typography: {
        fontFamily: [
          'Plus Jakarta Sans',
          '-apple-system',
          'BlinkMacSystemFont',
          '"Segoe UI"',
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif',
          '"Apple Color Emoji"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
        ].join(','),
      },
    });
  }, [mode, colors]); // Added colors to dependency array

  return (
    <ThemeContext.Provider value={{ mode, toggleColorMode, colors, setColors }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};